import * as THREE from 'three';
import type { EffectDimensions, SkillEffect } from './skill-effects.js';
import { effectMaterial, effectParticles, pathRibbon, shadeEffect } from './effect-surfaces.js';
import { lightningImpact } from './lightning-discharge.js';

/** Game seconds from the start of the cue until the bolt reaches its destination and gives the caster back. */
export const SPARK_STEP_ARRIVAL = .4;
const DEPART = .04, DURATION = .75;

/** Where the head of the bolt is at `t` seconds into the cue, from 0 at the start of the run to 1 at the destination. */
export function sparkStepTravel(t: number) {
  const s = THREE.MathUtils.clamp((t - DEPART) / (SPARK_STEP_ARRIVAL - DEPART), 0, 1);
  return 1 - (1 - s) ** 2.2;
}

/**
 * Spark Step: the Mage bursts into a bolt that races along the ground from where it stood to where it lands, carrying
 * a figure-tall column of current in its head, and breaks back into the caster with a second burst. The caster's figure
 * is hidden for the run by the scene's figure presence (`SPARK_STEP_ARRIVAL`); this draws only the electricity.
 */
export function sparkStep(dimensions: EffectDimensions): SkillEffect {
  const group = new THREE.Group(); group.name = 'spark-step';
  const height = dimensions.actorHeight, radius = dimensions.targetRadius;
  const ground = [pathRibbon(0xb6e0ff, .16, 4, 0xffffff, 28), pathRibbon(0x5d8dff, .5, 4, 0xe2efff, 28)];
  ground[0].mesh.name = 'spark-step-ground-bolt'; ground[1].mesh.userData.baseOpacity = .22;
  const forks = Array.from({ length: 4 }, (_, i) => {
    const path = pathRibbon(0xa9d3ff, .05, 4, 0xeef6ff, 8); path.mesh.userData.baseOpacity = .55;
    return { path, start: new THREE.Vector3(), end: new THREE.Vector3(), at: (i + .7) / 4.6, sign: i % 2 ? -1 : 1 };
  });
  const carrier = new THREE.Group(); carrier.name = 'spark-step-carrier';
  const column = new THREE.Mesh(new THREE.SphereGeometry(1, 24, 16), effectMaterial(0x9cccff, 0xffffff, 4, .06));
  column.scale.set(radius * .45, height * .5, radius * .45); column.position.y = height * .5; column.userData.baseOpacity = .8;
  const coils = Array.from({ length: 3 }, (_, i) => {
    const path = pathRibbon(0xc4e4ff, .06, 4, 0xffffff, 12); path.mesh.userData.baseOpacity = .8;
    return { path, phase: i * 2.1 };
  });
  carrier.add(column, effectParticles(0xd6ecff, 12, radius * .9, height, 1));
  const departure = lightningImpact(height, radius), arrival = lightningImpact(height, radius);
  departure.group.name = 'spark-step-departure'; arrival.group.name = 'spark-step-arrival';
  group.add(...ground.map(path => path.mesh), ...forks.map(fork => fork.path.mesh), ...coils.map(coil => coil.path.mesh), carrier, departure.group, arrival.group);
  const start = new THREE.Vector3(), head = new THREE.Vector3(), along = new THREE.Vector3(), base = new THREE.Vector3(), top = new THREE.Vector3(), zero = new THREE.Vector3();
  const light = { position: new THREE.Vector3(), color: new THREE.Color(0x9cc6ff), output: 0 };
  return { group, duration: DURATION * 1000, lights: [light, ...departure.lights!, ...arrival.lights!], update(p, from, to, time = p * DURATION, ppm = 40) {
    const t = p * DURATION, travel = sparkStepTravel(t), running = t < SPARK_STEP_ARRIVAL + .04;
    const afterglow = Math.max(0, 1 - (t - SPARK_STEP_ARRIVAL) / (DURATION - SPARK_STEP_ARRIVAL));
    start.copy(from).y += .12; head.copy(from).lerp(to, travel).y += .12;
    // The streak stays lit behind its head, and burns out from the start once the caster has arrived.
    const tail = THREE.MathUtils.clamp((t - SPARK_STEP_ARRIVAL * .7) / (DURATION - SPARK_STEP_ARRIVAL * .7), 0, 1) ** .7;
    start.lerp(head, tail);
    const flicker = .78 + .22 * Math.sin(time * 97);
    ground.forEach(path => { path.update(start, head, time, true, 0, 2.6); path.mesh.visible = afterglow > 0; shadeEffect(path.mesh, time, Math.min(1, t * 40) * afterglow * flicker, travel, ppm); });
    const dx = to.x - from.x, dz = to.z - from.z, distance = Math.hypot(dx, dz) || 1;
    forks.forEach(({ path, start: root, end, at, sign }, i) => {
      root.copy(from).lerp(to, at).y += .12;
      const reach = .45 + (i % 2) * .3, lit = travel > at;
      end.copy(root).addScaledVector(along.set(dx, 0, dz), .12 / distance * (1 + i * .2));
      end.x -= dz / distance * sign * reach; end.z += dx / distance * sign * reach;
      path.update(root, end, time, true, i, 1.6); path.mesh.visible = lit && afterglow > 0;
      shadeEffect(path.mesh, time, afterglow * flicker, 1, ppm);
    });
    carrier.position.copy(head).y -= .12; carrier.visible = running;
    column.scale.set(radius * (.35 + .15 * Math.sin(time * 61)), height * .5, radius * (.35 + .15 * Math.cos(time * 53)));
    shadeEffect(carrier, time, Math.min(1, t / DEPART) * (running ? 1 : 0), .5, ppm);
    coils.forEach(({ path, phase }) => {
      base.copy(carrier.position); base.x += Math.cos(time * 40 + phase) * radius * .4; base.z += Math.sin(time * 40 + phase) * radius * .4;
      top.copy(carrier.position); top.y += height * (.85 + .1 * Math.sin(time * 37 + phase)); top.x -= Math.cos(time * 33 + phase) * radius * .3;
      path.update(base, top, time, true, phase, 1.4); path.mesh.visible = running;
      shadeEffect(path.mesh, time, running ? flicker : 0, .5, ppm);
    });
    departure.update(Math.min(1, t / .45), zero, from, t, ppm); departure.group.visible = t < .45;
    const landed = (t - SPARK_STEP_ARRIVAL + .03) / (DURATION - SPARK_STEP_ARRIVAL + .03);
    arrival.update(THREE.MathUtils.clamp(landed, 0, 1), zero, to, Math.max(0, t - SPARK_STEP_ARRIVAL), ppm); arrival.group.visible = landed >= 0;
    if (landed < 0) arrival.lights![0].output = 0;
    if (t >= .45) departure.lights![0].output = 0;
    light.position.copy(carrier.position).y += height * .5; light.output = running ? 1.4 * flicker : 0;
  } };
}
